import React, { useState, useEffect, useRef, useCallback } from "react";
import Sketch from "react-p5";
import * as d3 from "d3";
import { Button, CircularProgress, Alert, Box } from "@mui/material";
import HolderListDialog from "./TokenHolderListDialog.component"; // Adjust path if needed
import { API_BASE_URL } from "../../utils/constants"; // Adjust path if needed

// --- Constants & Helper Functions ---

const MIN_RADIUS = 8;
const MAX_RADIUS = 65;
const RADIUS_SCALE_FACTOR = 2.0;
const MIN_ZOOM = 1;
const MAX_ZOOM = 25;
const TEXT_VISIBILITY_THRESHOLD_PX = 10; // Minimum screen radius to show text
const IMAGE_VISIBILITY_THRESHOLD_PX = 30; // Start loading/showing image when screen radius > this
const ZOOM_SENSITIVITY = 0.0015;
const IMAGE_API_PATH = 'https://images.odin.fun/token'; // *** ADJUST THIS API IMAGE PATH AS NEEDED ***

const DRAG_THRESHOLD = 5; // Pixel threshold to distinguish between a click and a drag

// Constants for the magnifying glass effect.
const MAG_RADIUS = 100; // Influence distance in world coordinates
const MAX_MAG = 1.3;   // Maximum magnification factor (i.e. effective scale)

const HUE_RANGE = [0, 360];
const SATURATION_RANGE = [0.55, 0.75];
const LIGHTNESS_RANGE = [0.8, 0.9];

// Calculate radius based on holder count using a logarithmic scale.
const calculateRadius = (holderCount) => {
    const count = holderCount || 0;
    const radius = MIN_RADIUS + Math.log1p(count) * (RADIUS_SCALE_FACTOR * 3);
    return Math.min(Math.max(radius, MIN_RADIUS), MAX_RADIUS);
};

// Generate a random number in a range.
const randomInRange = (min, max) => Math.random() * (max - min) + min;

// Generate a random pastel color in HSL format.
const getRandomPastelColor = () => {
    const hue = randomInRange(HUE_RANGE[0], HUE_RANGE[1]);
    const saturation = randomInRange(SATURATION_RANGE[0], SATURATION_RANGE[1]);
    const lightness = randomInRange(LIGHTNESS_RANGE[0], LIGHTNESS_RANGE[1]);
    return `hsl(${ hue }, ${ saturation * 100 }%, ${ lightness * 100 }%)`;
};
function getRandomRGBColor() {
    // Setting the minimum value to 180 ensures that each color channel is bright.
    const min = 180;
    // 256 is used because Math.random() * (max - min) yields values up to (but not including) (max - min)
    const max = 256;

    // Generate a random integer between min and 255 for each channel.
    const r = Math.floor(Math.random() * (max - min)) + min;
    const g = Math.floor(Math.random() * (max - min)) + min;
    const b = Math.floor(Math.random() * (max - min)) + min;

    // Return the color in the "rgb(r, g, b)" format.
    return `rgb(${ r }, ${ g }, ${ b })`;
}

// Example usage: log a random RGB color to the console.
// console.log(getRandomRGBColor());

// Default transform state.
const initialTransform = { k: 1, x: 0, y: 0 };

// --- Component Definition ---
const CommonHoldersBubbleMap = ({ tokenIdToFetch, width = 700, height = 500 }) => {
    const [apiData, setApiData] = useState(null);
    const [packedData, setPackedData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedTokenData, setSelectedTokenData] = useState(null);

    // Refs for p5 interaction state.
    const transformRef = useRef({ ...initialTransform });
    const isDraggingRef = useRef(false); // Tracks if the current interaction is considered a drag
    const lastMouseRef = useRef({ x: 0, y: 0 }); // Tracks the last mouse position during drag
    const initialMouseRef = useRef({ x: 0, y: 0 }); // Tracks the mouse position on press

    // Ref to store image loading state and loaded p5.Image objects.
    const imageStoreRef = useRef({});

    // Ref to store the p5 instance.
    const p5InstanceRef = useRef(null);

    // --- Helper: Clamp translation to keep content within the canvas ---
    const clampTranslation = useCallback((p5) => {
        if (!p5 || !packedData || packedData.length === 0) return;

        const k = transformRef.current.k;
        const canvasWidth = p5.width;
        const canvasHeight = p5.height;

        const minX = Math.min(...packedData.map((n) => n.x - n.r));
        const maxX = Math.max(...packedData.map((n) => n.x + n.r));
        const minY = Math.min(...packedData.map((n) => n.y - n.r));
        const maxY = Math.max(...packedData.map((n) => n.y + n.r));

        const contentWidth = k * (maxX - minX);
        const contentHeight = k * (maxY - minY);

        let newX = transformRef.current.x;
        let newY = transformRef.current.y;

        if (contentWidth < canvasWidth) {
            newX = (canvasWidth - k * (minX + maxX)) / 2;
        } else {
            const minAllowedX = canvasWidth - k * maxX;
            const maxAllowedX = -k * minX;
            newX = Math.min(Math.max(newX, minAllowedX), maxAllowedX);
        }
        if (contentHeight < canvasHeight) {
            newY = (canvasHeight - k * (minY + maxY)) / 2;
        } else {
            const minAllowedY = canvasHeight - k * maxY;
            const maxAllowedY = -k * minY;
            newY = Math.min(Math.max(newY, minAllowedY), maxAllowedY);
        }
        transformRef.current.x = newX;
        transformRef.current.y = newY;
    }, [packedData]);

    // --- Fetch Data ---
    const fetchData = useCallback(async (tokenId) => {
        if (!tokenId) {
            setApiData(null);
            setPackedData([]);
            setError(null);
            setLoading(false);
            return;
        }
        console.log(`Workspaceing data for token ID: ${ tokenId }`);
        setLoading(true);
        setError(null);
        setPackedData([]);
        transformRef.current = { ...initialTransform };
        imageStoreRef.current = {};

        try {
            const response = await fetch(`${ API_BASE_URL }/tokens/${ tokenId }/connections`);
            if (!response.ok) {
                throw new Error(`API Error ${ response.status }: ${ response.statusText }`);
            }
            const result = await response.json();
            console.log("API response:", result);

            const processedData = Object.entries(result).reduce((acc, [id, tokenInfo]) => {
                acc[id] = { ...tokenInfo, holders: tokenInfo.holders || [] };
                return acc;
            }, {});

            setApiData(processedData);
        } catch (err) {
            console.error("Fetch Error:", err);
            setError(err.message || "Failed to fetch data.");
            setApiData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // --- Effect to Fetch Data ---
    useEffect(() => {
        fetchData(tokenIdToFetch);
    }, [tokenIdToFetch, fetchData]);

    // --- Effect to Compute Layout ---
    useEffect(() => {
        if (!apiData || Object.keys(apiData).length === 0) {
            setPackedData([]);
            return;
        }

        const nodes = Object.entries(apiData)
            .map(([id, tokenInfo]) => {
                if (!tokenInfo.details) return null;
                const holderCount = tokenInfo.holders?.length || 0;
                const r = calculateRadius(holderCount);
                if (r < MIN_RADIUS) return null;
                return {
                    id,
                    details: tokenInfo.details,
                    holderCount,
                    r,
                    color: getRandomRGBColor(), // Unique color for each bubble
                };
            })
            .filter(Boolean);

        if (nodes.length === 0) {
            setPackedData([]);
            return;
        }

        const hierarchyData = { id: "root", children: nodes.map(node => ({ value: node.r * node.r, originalData: node })) };
        const root = d3.hierarchy(hierarchyData)
            .sum(d => Math.max(1, d.value))
            .sort((a, b) => b.value - a.value);
        const packLayout = d3.pack().size([width, height]).padding(5);
        const packedRoot = packLayout(root);

        const newPacked = packedRoot.descendants()
            .slice(1)
            .map(d => ({
                x: d.x,
                y: d.y,
                r: d.r,
                id: d.data.originalData.id,
                details: d.data.originalData.details,
                holderCount: d.data.originalData.holderCount,
                color: d.data.originalData.color,
            }));

        console.log("Packed data calculated:", newPacked);
        setPackedData(newPacked);
        transformRef.current = { ...initialTransform };
    }, [apiData, width, height]);

    // --- p5.js Sketch Functions ---

    const setup = useCallback((p5, canvasParentRef) => {
        p5.createCanvas(width, height).parent(canvasParentRef);
        p5InstanceRef.current = p5;
        console.log("p5 setup complete");
    }, [width, height]);

    const draw = useCallback((p5) => {
        if (!p5 || !packedData || packedData.length === 0) {
            p5?.clear();
            return;
        }
        p5.clear();
        const { k, x, y } = transformRef.current;

        const worldMouseX = (p5.mouseX - x) / k;
        const worldMouseY = (p5.mouseY - y) / k;

        const drawNodes = packedData.map(node => {
            const dx = worldMouseX - node.x;
            const dy = worldMouseY - node.y;
            const d = Math.sqrt(dx * dx + dy * dy);
            const magFactor = d < MAG_RADIUS ? 1 + (MAX_MAG - 1) * (1 - d / MAG_RADIUS) : 1;
            return {
                id: node.id,
                x: node.x,
                y: node.y,
                effectiveR: node.r * magFactor,
                node: node,
                offsetX: 0,
                offsetY: 0,
            };
        });

        // Collision Resolution
        const iterations = 3;
        for (let iter = 0; iter < iterations; iter++) {
            for (let i = 0; i < drawNodes.length; i++) {
                for (let j = i + 1; j < drawNodes.length; j++) {
                    const nodeA = drawNodes[i];
                    const nodeB = drawNodes[j];
                    const ax = nodeA.x + nodeA.offsetX;
                    const ay = nodeA.y + nodeA.offsetY;
                    const bx = nodeB.x + nodeB.offsetX;
                    const by = nodeB.y + nodeB.offsetY;
                    const dx = bx - ax;
                    const dy = by - ay;
                    const distSq = dx * dx + dy * dy;
                    const minDist = nodeA.effectiveR + nodeB.effectiveR;
                    if (distSq < minDist * minDist && distSq > 1e-6) {
                        const dist = Math.sqrt(distSq);
                        const overlap = (minDist - dist) / 2;
                        const nx = dx / dist;
                        const ny = dy / dist;
                        nodeA.offsetX -= nx * overlap;
                        nodeA.offsetY -= ny * overlap;
                        nodeB.offsetX += nx * overlap;
                        nodeB.offsetY += ny * overlap;
                    }
                }
            }
        }

        // Drawing Phase
        p5.push();
        p5.translate(x, y);
        p5.scale(k);
        p5.imageMode(p5.CENTER);

        drawNodes.forEach((drawNode) => {
            const finalX = drawNode.x + drawNode.offsetX;
            const finalY = drawNode.y + drawNode.offsetY;
            const effectiveR = drawNode.effectiveR;
            const baseNode = drawNode.node;

            // Image Loading
            const imageStore = imageStoreRef.current;
            if (!imageStore[baseNode.id]) {
                imageStore[baseNode.id] = { status: "idle", img: null };
            }
            const imageStatus = imageStore[baseNode.id];
            const screenRadius = baseNode.r * k;
            const screenX = (finalX * k) + x;
            const screenY = (finalY * k) + y;
            const isOnScreen =
                screenX + screenRadius > 0 && screenX - screenRadius < p5.width &&
                screenY + screenRadius > 0 && screenY - screenRadius < p5.height;
            const shouldLoadImage = screenRadius > IMAGE_VISIBILITY_THRESHOLD_PX * 0.8;
            const shouldShowImage = imageStatus.status === "loaded" && imageStatus.img && screenRadius > IMAGE_VISIBILITY_THRESHOLD_PX;

            if (imageStatus.status === "idle" && isOnScreen && shouldLoadImage) {
                imageStatus.status = "loading";
                const imageUrl = `${ IMAGE_API_PATH }/${ baseNode.id }`;
                p5.loadImage(
                    imageUrl,
                    (loadedImg) => {
                        if (!p5InstanceRef.current) return;
                        if (imageStoreRef.current[baseNode.id]?.status === 'loading') {
                            imageStoreRef.current[baseNode.id] = { status: "loaded", img: loadedImg };
                        }
                    },
                    (err) => {
                        console.error(`Failed to load image for ${ baseNode.id }:`, err);
                        if (!p5InstanceRef.current) return;
                        if (imageStoreRef.current[baseNode.id]?.status === 'loading') {
                            imageStoreRef.current[baseNode.id] = { status: "error", img: null };
                        }
                    }
                );
            }

            // Draw Bubble
            p5.push();
            p5.translate(finalX, finalY);

            if (shouldShowImage) {
                p5.push();
                p5.drawingContext.beginPath();
                p5.drawingContext.arc(0, 0, effectiveR, 0, p5.TWO_PI);
                p5.drawingContext.closePath();
                p5.drawingContext.clip();
                const img = imageStatus.img;
                let imgWidth, imgHeight;
                const aspectRatio = img.width / img.height;
                if (aspectRatio > 1) {
                    imgHeight = effectiveR * 2;
                    imgWidth = imgHeight * aspectRatio;
                } else {
                    imgWidth = effectiveR * 2;
                    imgHeight = imgWidth / aspectRatio;
                }
                p5.image(img, 0, 0, imgWidth, imgHeight);
                p5.pop(); // remove clip
                p5.noFill();
                p5.stroke(50);
                p5.strokeWeight(1.5 / k);
                p5.ellipse(0, 0, effectiveR * 2);
            } else {
                p5.fill(baseNode.color);
                p5.stroke(50);
                p5.strokeWeight(1 / k);
                p5.ellipse(0, 0, effectiveR * 2);
            }

            // Draw Text
            const screenEffectiveRadius = effectiveR * k;
            if (screenEffectiveRadius > TEXT_VISIBILITY_THRESHOLD_PX) {
                let name = baseNode.details?.ticker || baseNode.details?.name || "";
                let totalHolders = baseNode.holderCount || 0;
                if (name) {
                    p5.noStroke();
                    p5.fill(shouldShowImage ? 255 : 0);
                    p5.drawingContext.shadowOffsetX = 1 / k;
                    p5.drawingContext.shadowOffsetY = 1 / k;
                    p5.drawingContext.shadowBlur = 2 / k;
                    p5.drawingContext.shadowColor = shouldShowImage ? "rgba(0,0,0,0.8)" : "rgba(255,255,255,0.7)";
                    p5.textAlign(p5.CENTER, p5.CENTER);
                    const maxTextWidth = effectiveR * 1.6;
                    let fontSize = Math.min(effectiveR * 0.6, screenEffectiveRadius * 0.4);
                    fontSize = Math.max(fontSize, 6 / k);
                    p5.textSize(fontSize);
                    while (p5.textWidth(name) > maxTextWidth && fontSize > 5 / k) {
                        fontSize *= 0.9;
                        p5.textSize(fontSize);
                    }
                    p5.text(name, 0, 0);
                    if (totalHolders > 0) {
                        p5.textSize(fontSize * 0.6);
                        p5.textAlign(p5.CENTER, p5.BOTTOM);
                        p5.text(totalHolders, 0, effectiveR * 0.6);
                    }
                    p5.drawingContext.shadowColor = "rgba(0,0,0,0)";
                    p5.drawingContext.shadowOffsetX = 0;
                    p5.drawingContext.shadowOffsetY = 0;
                    p5.drawingContext.shadowBlur = 0;
                }
            }
            p5.pop(); // bubble translate
        });

        p5.pop(); // global transform
    }, [packedData, apiData, width, height]);

    // --- Cleanup Effect ---
    useEffect(() => {
        return () => {
            if (p5InstanceRef.current) {
                console.log("Removing p5 instance");
                p5InstanceRef.current.remove();
                p5InstanceRef.current = null;
            }
            imageStoreRef.current = {};
            console.log("Component unmounted, p5 instance removed.");
        };
    }, []);

    // --- p5.js Event Handlers ---
    const mouseWheel = useCallback((p5, event) => {
        // Get the canvas element from the p5 instance safely
        const canvasElement = p5?.canvas;

        // Check if the canvas element exists and the event target *is* the canvas
        // Using event.target === canvasElement is slightly more specific than closest()
        // if you only want to capture events directly on the canvas itself.
        // closest('canvas') is also fine if you might have child elements within the p5 structure
        // that should also trigger zoom without page scroll.
        if (canvasElement && event.target === canvasElement) { // Or use: event.target.closest('canvas')

            // *** PREVENT BROWSER DEFAULT SCROLL ***
            event.preventDefault();
            // **************************************

            const current = transformRef.current;
            // Use the original browser event's clientX/Y for accuracy relative to the viewport
            const { clientX, clientY, deltaY } = event;
            const rect = canvasElement.getBoundingClientRect(); // Get canvas position relative to viewport
            const mouseX = clientX - rect.left; // Mouse X relative to canvas top-left
            const mouseY = clientY - rect.top;  // Mouse Y relative to canvas top-left

            // Your existing zoom logic
            let newK = current.k * Math.exp(-deltaY * ZOOM_SENSITIVITY);
            newK = p5.constrain(newK, MIN_ZOOM, MAX_ZOOM);
            const worldX = (mouseX - current.x) / current.k;
            const worldY = (mouseY - current.y) / current.k;
            const newX = mouseX - worldX * newK;
            const newY = mouseY - worldY * newK;

            transformRef.current = { k: newK, x: newX, y: newY };
            clampTranslation(p5);

            // Tell p5.js that the event has been handled and shouldn't propagate further (within p5)
            // This is good practice but event.preventDefault() is what stops the *browser* scroll.
            return false;
        }

        // If the mouse wheel event didn't happen on our canvas,
        // allow the default browser behavior (page scroll).
        return true;
    }, [clampTranslation]); // Dependency array remains the same

    const mousePressed = useCallback((p5) => {
        const p5Instance = p5InstanceRef.current;
        if (!p5Instance) return;

        if (p5Instance.mouseX >= 0 && p5Instance.mouseX <= p5Instance.width && p5Instance.mouseY >= 0 && p5Instance.mouseY <= p5Instance.height) {
            initialMouseRef.current = { x: p5Instance.mouseX, y: p5Instance.mouseY };
            lastMouseRef.current = { x: p5Instance.mouseX, y: p5Instance.mouseY };
            isDraggingRef.current = false;
        } else {
            isDraggingRef.current = false;
        }
    }, []);

    const mouseDragged = useCallback((p5) => {
        const p5Instance = p5InstanceRef.current;
        if (!p5Instance) return;

        if (initialMouseRef.current) {
            const currentMouse = { x: p5Instance.mouseX, y: p5Instance.mouseY };
            const dxTotal = currentMouse.x - initialMouseRef.current.x;
            const dyTotal = currentMouse.y - initialMouseRef.current.y;

            if (!isDraggingRef.current && (dxTotal * dxTotal + dyTotal * dyTotal) >= DRAG_THRESHOLD * DRAG_THRESHOLD) {
                isDraggingRef.current = true;
            }

            if (isDraggingRef.current) {
                const dx = currentMouse.x - lastMouseRef.current.x;
                const dy = currentMouse.y - lastMouseRef.current.y;

                transformRef.current.x += dx;
                transformRef.current.y += dy;

                lastMouseRef.current = currentMouse;
                clampTranslation(p5Instance);
            }
        }
    }, [clampTranslation]);

    const mouseReleased = useCallback((p5, event) => {
        const p5Instance = p5InstanceRef.current;
        if (!p5Instance) return;

        if (isDraggingRef.current) {
            isDraggingRef.current = false;
        } else {
            let releaseX = event?.offsetX ?? p5Instance.mouseX;
            let releaseY = event?.offsetY ?? p5Instance.mouseY;

            if (releaseX < 0 || releaseX > p5Instance.width || releaseY < 0 || releaseY > p5Instance.height) {
                isDraggingRef.current = false;
                return;
            }

            const { k, x, y } = transformRef.current;
            const worldX = (releaseX - x) / k;
            const worldY = (releaseY - y) / k;

            let clickedNodeData = null;
            let clickedNodeId = null;

            for (let i = packedData.length - 1; i >= 0; i--) {
                const node = packedData[i];
                const dxNode = worldX - node.x;
                const dyNode = worldY - node.y;
                const distSqNode = dxNode * dxNode + dyNode * dyNode;
                const dClick = Math.sqrt(distSqNode);
                const magFactorClick = dClick < MAG_RADIUS ? 1 + (MAX_MAG - 1) * (1 - dClick / MAG_RADIUS) : 1;
                const effectiveRClick = node.r * magFactorClick;

                if (distSqNode <= effectiveRClick * effectiveRClick) {
                    clickedNodeId = node.id;
                    if (apiData && apiData[clickedNodeId]) {
                        clickedNodeData = apiData[clickedNodeId];
                    }
                    break;
                }
            }

            if (clickedNodeData) {
                setSelectedTokenData(clickedNodeData);
                setDialogOpen(true);
            }
            isDraggingRef.current = false;
        }
    }, [packedData, apiData, setDialogOpen, setSelectedTokenData]);

    // --- Render ---
    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: 2 }}>
            {/* <Button
                variant="contained"
                onClick={() => fetchData(tokenIdToFetch)}
                disabled={loading || !tokenIdToFetch}
            >
                {loading ? "Loading..." : "Refresh Data"}
            </Button> */}

            {error && (
                <Alert severity="error" sx={{ width: "100%", maxWidth: `${ width }px` }}>
                    {error}
                </Alert>
            )}

            <Box sx={{
                width: width,
                height: height,
                position: "relative",
                border: "1px solid #ccc",
                overflow: "hidden"
            }}>
                {loading && (
                    <Box sx={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "rgba(255, 255, 255, 0.7)", zIndex: 10 }}>
                        <CircularProgress />
                    </Box>
                )}

                {width > 0 && height > 0 && (
                    <Sketch
                        setup={setup}
                        draw={draw}
                        mouseWheel={mouseWheel}
                        mousePressed={mousePressed}
                        mouseDragged={mouseDragged}
                        mouseReleased={mouseReleased}
                    />
                )}

                {!loading && !error && packedData.length === 0 && apiData && Object.keys(apiData).length > 0 && (
                    <Box sx={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center", color: "#888", textAlign: "center", padding: 2, zIndex: 5 }}>
                        <p>No common holder data found meeting the criteria or all nodes were filtered out.</p>
                    </Box>
                )}
                {!loading && !error && (!apiData || Object.keys(apiData).length === 0) && (
                    <Box sx={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center", color: "#888", textAlign: "center", padding: 2, zIndex: 5 }}>
                        <p>No data available for the selected token ID.</p>
                    </Box>
                )}
            </Box>

            <HolderListDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                tokenData={selectedTokenData}
            />
        </Box>
    );
};

export default CommonHoldersBubbleMap;
